import { z } from 'zod';

export const EMAIL_SECRET_FIELDS = [
  'password',
  'clientSecret',
  'accessToken',
  'refreshToken',
  'privateKey',
] as const;

export const notificationEmailAuthZod = z
  .object({
    host: z.string().trim().min(1, 'SMTP host is required'),
    port: z.number().int().min(1).max(65535),
    type: z.enum(['login', 'oauth2']),
    user: z.string().trim().min(1, 'SMTP user is required'),
    password: z.string().nullable().optional(),
    clientId: z.string().nullable().optional(),
    clientSecret: z.string().nullable().optional(),
    accessToken: z.string().nullable().optional(),
    refreshToken: z.string().nullable().optional(),
    serviceClient: z.string().nullable().optional(),
    privateKey: z.string().nullable().optional(),
    tls: z.string().nullable().optional(),
    oauthFlow: z.enum(['token', '2lo', '3lo']).optional(),
  })
  .strict();

export const notificationEmailSettingsZod = z
  .object({
    enabled: z.boolean(),
    userEntityEmailField: z
      .string()
      .trim()
      .min(1, 'User email field is required'),
    senderName: z.string().trim().min(1, 'Sender name is required'),
    senderEmail: z
      .string()
      .trim()
      .refine(
        value => value === '' || z.string().email().safeParse(value).success,
        'Sender email must be a valid email address',
      )
      .optional(),
    auth: notificationEmailAuthZod.optional(),
  })
  .strict()
  .superRefine((settings, context) => {
    if (settings.enabled && !settings.auth) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['auth'],
        message: 'SMTP provider settings are required when email is enabled',
      });
    }
  });

export type NotificationEmailAuth = z.infer<typeof notificationEmailAuthZod>;
export type NotificationEmailSettings = z.infer<
  typeof notificationEmailSettingsZod
>;

export function resolveEmailOAuthFlow(
  auth: NotificationEmailAuth,
): 'token' | '2lo' | '3lo' {
  if (auth.oauthFlow) return auth.oauthFlow;
  if (auth.serviceClient || auth.privateKey) return '2lo';
  if (auth.clientId || auth.clientSecret || auth.refreshToken) return '3lo';
  return 'token';
}

export function getActiveEmailSecretFields(
  auth: NotificationEmailAuth,
): readonly (typeof EMAIL_SECRET_FIELDS)[number][] {
  if (auth.type === 'login') return ['password'];
  const flow = resolveEmailOAuthFlow(auth);
  if (flow === '2lo') return ['privateKey'];
  if (flow === '3lo') return ['clientSecret', 'refreshToken'];
  return ['accessToken'];
}

export function getEmailAuthIdentity(
  auth?: NotificationEmailAuth,
): string | undefined {
  if (!auth) return undefined;
  const flow = auth.type === 'oauth2' ? resolveEmailOAuthFlow(auth) : undefined;
  let client: string | null | undefined;
  if (flow === '2lo') client = auth.serviceClient;
  else if (flow === '3lo') client = auth.clientId;
  return JSON.stringify([
    auth.type,
    flow,
    auth.host.trim(),
    auth.user.trim(),
    client ?? '',
  ]);
}

export type ResolvedNotificationEmailAuth = {
  [Field in keyof NotificationEmailAuth]: Exclude<
    NotificationEmailAuth[Field],
    null
  >;
};

export function mergeEmailAuthSecrets(
  incoming?: NotificationEmailAuth,
  existing?: NotificationEmailAuth,
): ResolvedNotificationEmailAuth | undefined {
  const source = incoming ?? existing;
  if (!source) return undefined;
  const merged: ResolvedNotificationEmailAuth = {
    ...source,
    password: source.password ?? undefined,
    clientId: source.clientId ?? undefined,
    clientSecret: source.clientSecret ?? undefined,
    accessToken: source.accessToken ?? undefined,
    refreshToken: source.refreshToken ?? undefined,
    serviceClient: source.serviceClient ?? undefined,
    privateKey: source.privateKey ?? undefined,
    tls: source.tls ?? undefined,
  };
  if (!incoming) return merged;
  const activeFields = getActiveEmailSecretFields(incoming);
  const canReuse =
    getEmailAuthIdentity(incoming) === getEmailAuthIdentity(existing);
  for (const field of EMAIL_SECRET_FIELDS) {
    if (!activeFields.includes(field) || incoming[field] === null) {
      delete merged[field];
    } else if (!incoming[field]) {
      const saved = canReuse ? existing?.[field] : undefined;
      if (saved) merged[field] = saved;
      else delete merged[field];
    }
  }
  if (!merged.tls) delete merged.tls;
  return merged;
}

export const notificationEmailDeliveryZod =
  notificationEmailSettingsZod.superRefine((settings, context) => {
    if (!settings.enabled || !settings.auth) return;
    const auth = settings.auth;
    const required = [...getActiveEmailSecretFields(auth)];
    const flow =
      auth.type === 'oauth2' ? resolveEmailOAuthFlow(auth) : undefined;
    const requiredFields: (keyof NotificationEmailAuth)[] = [...required];
    if (flow === '2lo') requiredFields.push('serviceClient');
    if (flow === '3lo') requiredFields.push('clientId');
    const labels: Partial<Record<keyof NotificationEmailAuth, string>> = {
      password: 'Password',
      accessToken: 'Access token',
      privateKey: 'Private key',
      serviceClient: 'Service client',
      clientId: 'Client ID',
      clientSecret: 'Client secret',
      refreshToken: 'Refresh token',
    };
    for (const field of requiredFields) {
      if (!auth[field])
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['auth', field],
          message: `${labels[field]} is required for the selected authentication method`,
        });
    }
  });
